// Contexte d'authentification global.
// Gère : session persistante (cookies JWT), récupération de l'entreprise,
// rafraîchissement automatique du jeton et déconnexion après 30 min
// d'inactivité.
import React, { createContext, useContext, useEffect, useCallback, useState, useRef } from "react";
import api, { formatErreur } from "@/lib/api";

const AuthContext = createContext(null);

// Délai d'inactivité avant déconnexion automatique (30 minutes)
const DELAI_INACTIVITE = 30 * 60 * 1000;

export function AuthProvider({ children }) {
  // user : null (vérification en cours) | objet (connecté) | false (déconnecté)
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [companyChargee, setCompanyChargee] = useState(false);
  const [abonnement, setAbonnement] = useState(null);
  const [acces, setAcces] = useState(null);
  const minuteurInactivite = useRef(null);

  // --- Récupération de l'abonnement courant ---
  const chargerAbonnement = useCallback(async () => {
    try {
      const { data } = await api.get("/abonnements/me");
      setAbonnement(data.abonnement);
      setAcces(data.acces);
    } catch {
      setAbonnement(null);
      setAcces(null);
    }
  }, []);

  // --- Récupération de l'entreprise de l'utilisateur ---
  const chargerEntreprise = useCallback(async () => {
    try {
      const { data } = await api.get("/companies/me");
      setCompany(data);
      if (data) await chargerAbonnement();
    } catch {
      setCompany(null);
    } finally {
      setCompanyChargee(true);
    }
  }, [chargerAbonnement]);

  // --- Déconnexion ---
  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* on ignore les erreurs de déconnexion */
    }
    setUser(false);
    setCompany(null);
    setCompanyChargee(false);
  }, []);

  // --- Vérification de la session au démarrage ---
  const verifierSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await chargerEntreprise();
    } catch {
      // Tentative de rafraîchissement du jeton
      try {
        await api.post("/auth/refresh");
        const { data } = await api.get("/auth/me");
        setUser(data);
        await chargerEntreprise();
      } catch {
        setUser(false);
      }
    }
  }, [chargerEntreprise]);

  useEffect(() => {
    verifierSession();
  }, [verifierSession]);

  // --- Minuteur d'inactivité (déconnexion auto après 30 min) ---
  useEffect(() => {
    if (!user) return undefined;

    const reinitialiser = () => {
      if (minuteurInactivite.current) clearTimeout(minuteurInactivite.current);
      minuteurInactivite.current = setTimeout(() => {
        logout();
      }, DELAI_INACTIVITE);
    };

    const evenements = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evenements.forEach((e) => window.addEventListener(e, reinitialiser));
    reinitialiser();

    return () => {
      evenements.forEach((e) => window.removeEventListener(e, reinitialiser));
      if (minuteurInactivite.current) clearTimeout(minuteurInactivite.current);
    };
  }, [user, logout]);

  // --- Connexion ---
  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      await chargerEntreprise();
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, message: formatErreur(e.response?.data?.detail) };
    }
  };

  // --- Inscription ---
  const register = async (email, password, accepterCgu) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, accepter_cgu: accepterCgu });
      setUser(data);
      await chargerEntreprise();
      return { ok: true };
    } catch (e) {
      return { ok: false, message: formatErreur(e.response?.data?.detail) };
    }
  };

  const valeur = {
    user,
    company,
    companyChargee,
    login,
    register,
    logout,
    chargerEntreprise,
    chargerAbonnement,
    abonnement,
    acces,
    estAdmin: user && typeof user === "object" && user.role === "admin",
    setCompany,
  };

  return <AuthContext.Provider value={valeur}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth doit être utilisé dans AuthProvider");
  return ctx;
}
